import { ReactNode, Component, ReactElement } from 'react';
import _ from 'lodash';
import { DebugEx } from "@Utils/DebugEx";
import { isDefined, IsNullOrEmpty, IsNullOrWhiteSpace } from "@Utils/Micro";
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { getButtonElement, getButtonSettingsListInfo } from '@UI/Widgets/Things/UserInput/FormButtonsUtils';
import { ButtonThemeType, ButtonSettings } from '@UI/Widgets/Things/UserInput/FormStruct';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { Timeline, TimelineInfoItemBody, TimelineInfoItemFooter, TimelineInfoItemHeader, TimelineInfoObject } from '@Primitive/Timeline';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { ReduxThingState, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { WebNode } from '@API/WebNode';
import { Plegma } from '@API/Api';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import { StateManager } from "@Utils/StateManager";
import { RamStorage } from '@Utils/RamStorage';
// =======================================================================================================
export interface TimeLineData {
    Events: TimeLineEvent[];
    EventIconList: TimeLineEventIcon[];
    EventColorList: TimeLineEventColor[];
    FooterButtons: ButtonSettings[];
    LimitedEvents: boolean;
    MaxEvents: number;
}

export interface TimeLineState {
    TimeLineSettings: TimeLineSettings
    TimeLineInfo: TimelineInfoObject[];
}

export interface TimeLineSettings {
    Events: TimeLineEvent[];
    EventIconList: TimeLineEventIcon[];
    EventColorList: TimeLineEventColor[];
    TimeLineItemStruct: TimeLineItemStruct[];
    FooterButtons: ButtonSettings[];
}

export interface TimeLineEvent {
    name: string;
    icon: string;
    iconTheme: string;
}

export interface TimeLineEventIcon {
    value: string;
    label: string;
}

export interface TimeLineEventColor {
    value: string;
    label: string;
}

export interface TimeLineItemStruct {
    event: string;
    timestamp: string;
    dateTime?: Date;
    header: TimelineInfoItemHeader;
    body?: TimelineInfoItemBody;
    footer?: TimelineInfoItemFooter;
}

export interface TimeLineInfo {
    startDate: string,
    endDate: string,
    items: TimeLineItem[]
}

export interface TimeLineItem {
    icon: string,
    iconTheme: string,
    time: string,

    header: { url: string, title: string, content: string, markup?: ReactElement[] },
    body?: { content: string, markup?: ReactElement[] },
    footer?: { content: string, markup?: ReactElement[] }
}

export interface OutputJSON {
    PortKey: string,
    HeaderTitle: string,
    Timestamp: string
}

// create TimeLineItemJsonScheme class for json validation
export function TimeLineItemJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("TimeLineItemJsonSchemeValidator", {
        title: "SelectDataType",
        type: "array",
        items: {
            type: "object",
            properties: {
                event: { type: "string" },
                timestamp: { type: "string" },
                header: {
                    type: "object",
                    properties: {
                        url: { type: "string" },
                        title: { type: "string" },
                        content: { type: "string" }
                    },
                    required: ["title", "content"]
                },
                body: {
                    type: "object",
                    properties: {
                        content: { type: "string" }
                    },
                    required: ["content"]
                },
                footer: {
                    type: "object",
                    properties: {
                        content: { type: "string" }
                    },
                    required: ["content"]
                },
            },
            required: ["event", "header"]
        },
        minItems: 0,
    });
}
// ------------------------------------------------------------------------------------------------------
class TimeLine extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<TimeLineData>, TimeLineState> {
    // ---------------------------------------------------------------------------------------------------
    private StateStorage: RamStorage;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<TimeLineData>) {
        super(props);

        let data: TimeLineItemStruct[] = [];
        if (_.has(this.props.WidgetDefaultValues, "set")) {
            data = this.props.WidgetDefaultValues["set"];
        }

        if (isDefined(props.PortsStates)) {
            if (_.has(props.PortsStates, "set")) {
                let port = props.PortsStates["set"];
                if (port.LastStateStatus == StateStatus.Valid)
                    data = props.PortsStates["set"].State;
            }
        }

        this.StateStorage = RamStorage.GetInstance();
        let savedSettings: TimeLineSettings = this.StateStorage.GetState(this.props.ThingKey);
        if (savedSettings == null) {
            savedSettings = {
                Events: this.props.WidgetData.Events,
                EventIconList: this.props.WidgetData.EventIconList,
                EventColorList: this.props.WidgetData.EventColorList,
                TimeLineItemStruct: data,
                FooterButtons: this.props.WidgetData.FooterButtons
            }
        }

        this.state = {
            TimeLineSettings: savedSettings,
            TimeLineInfo: this.initTimeLine(savedSettings, this.props)
        };
    }
    // ---------------------------------------------------------------------------------------------------
    initTimeLine(settings: TimeLineSettings, props: WidgetsThingsStructs.PartialIWidgetThingsProps<TimeLineData>): TimeLineInfo[] {
        if (settings.TimeLineItemStruct.length > 0) {
            // create items for Timeline object
            var items: TimeLineItem[] = _.map(settings.TimeLineItemStruct, (element, index) => {
                let evt = _.find(settings.Events, function (w) { return w.name == element.event; });
                let newItem: TimeLineItem = {
                    icon: evt == null ? "" : evt.icon,
                    iconTheme: evt == null ? "bg-grey" : evt.iconTheme,
                    body: element.body,
                    time: element.timestamp,
                    header: element.header,
                    footer: null
                }
                if (settings.FooterButtons.length > 0) {
                    newItem.footer = {
                        content: "",
                        markup: this.getFooterElements(newItem, _.cloneDeep(settings.FooterButtons), props)
                    };
                }
                return newItem;
            });

            return [{
                startDate: items[items.length - 1].time,
                endDate: items[0].time,
                items: items
            }];
        } else {
            var previousDay = new Date();
            previousDay.setDate(previousDay.getDate() - 1);
            var now = new Date();
            return [{
                startDate: now.toISOString(),
                endDate: previousDay.toISOString(),
                items: []
            }];
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        if (this.state.TimeLineInfo == null)
            return WidgetsThingsStructs.DefaultMessageOnNoData(this.props.width, this.props.height);

        return (
            <div style={{ padding: WidgetsThingsStructs.defaultWidgetContainerPadding }}>
                <Timeline timelineInfo={this.state.TimeLineInfo} />
            </div>
        );
    }
    // ------------------------------------------------------------------------------------------------
    getFooterElements(
        item: TimeLineItem,
        FooterButtons: ButtonSettings[],
        props: WidgetsThingsStructs.PartialIWidgetThingsProps<TimeLineData>
    ): ReactElement[] {
        return _.map(FooterButtons, (b) => {
            // try get translation
            const label = props.LocalizationMap['button_' + b.Key];
            if (!IsNullOrEmpty(label)) {
                b.Label = label;
            }
            return getButtonElement(b, 0, 0, () => this.updateOutputPort(item, b.Key));
        });
    }
    // ------------------------------------------------------------------------------------------------
    updateOutputPort(item: TimeLineItem, portKey: string) {
        let output: OutputJSON = {
            PortKey: portKey,
            HeaderTitle: item.header.title + " " + item.header.content,
            Timestamp: item.time
        };
        this.props.SendPortEvent(portKey, JSON.stringify(output));
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<TimeLineData>) {
        if (!_.isEqual(nextProps.WidgetData, this.props.WidgetData)) {
            this.setState((prevState: TimeLineState) => {
                prevState.TimeLineSettings.Events = nextProps.WidgetData.Events;
                prevState.TimeLineSettings.EventIconList = nextProps.WidgetData.EventIconList;
                prevState.TimeLineSettings.EventColorList = nextProps.WidgetData.EventColorList;
                prevState.TimeLineSettings.FooterButtons = nextProps.WidgetData.FooterButtons;
                prevState.TimeLineInfo = this.initTimeLine(prevState.TimeLineSettings, nextProps);
                return prevState;
            });
        }

        if (!_.isEqual(nextProps.LocalizationMap, this.props.LocalizationMap)) {
            this.setState((prevState: TimeLineState) => {
                prevState.TimeLineInfo = this.initTimeLine(prevState.TimeLineSettings, nextProps);
                return prevState;
            });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private updateStateFromData(prevState: TimeLineState, data: TimeLineItemStruct[]): TimeLineState {
        if (data.length > 0) {
            const currentTimestamp = new Date().toISOString();

            data.forEach(element => {
                // assign now() as timestamp if not specified
                element.timestamp = isDefined(element.timestamp) ?
                    element.timestamp.replace(new RegExp("/", 'g'), "-") :
                    currentTimestamp;

                element.dateTime = new Date(Date.parse(element.timestamp));

                // assign # as header.url if not specified
                if (!isDefined(element.header) ||
                    IsNullOrEmpty(element.header.url) ||
                    IsNullOrWhiteSpace(element.header.url))
                    element.header.url = null;

                prevState.TimeLineSettings.TimeLineItemStruct.push(element);
            });
        }

        prevState.TimeLineSettings.TimeLineItemStruct = _.reverse(_.sortBy(prevState.TimeLineSettings.TimeLineItemStruct, "dateTime"));
        if (this.props.WidgetData.LimitedEvents) {
            if (isDefined(prevState.TimeLineSettings.TimeLineItemStruct) &&
                prevState.TimeLineSettings.TimeLineItemStruct.length > this.props.WidgetData.MaxEvents) {

                let diffInLength = prevState.TimeLineSettings.TimeLineItemStruct.length - this.props.WidgetData.MaxEvents;
                for (let i = 0; i < diffInLength; i++) {
                    prevState.TimeLineSettings.TimeLineItemStruct.pop();
                }
            }
        }
        prevState.TimeLineInfo = this.initTimeLine(prevState.TimeLineSettings, this.props);
        return prevState;
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        // Link update port
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.RegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.UnRegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------
    stateUpdate = (state: ReduxThingState, forceUpdate: boolean, changedSubKeys: string[]) => {
        for (const portKey of changedSubKeys) {
            let port = state.PortStates[portKey];
            if (port != null)
                this.UpdatePort(portKey, port.State, port.LastStateStatus == StateStatus.Valid);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    public UpdatePort(portKey: string, portState: any, isValid: boolean) {
        try {
            if (portKey == "reset") {
                this.setState((prevState) => {
                    prevState.TimeLineSettings.TimeLineItemStruct = [];
                    prevState = this.updateStateFromData(prevState, []);
                    this.StateStorage.SetState(this.props.ThingKey, prevState.TimeLineSettings);
                    return prevState;
                });
            } else if (portKey == "add") {
                if (isValid)
                    this.setState((prevState) => {
                        prevState = this.updateStateFromData(prevState, portState);
                        this.StateStorage.SetState(this.props.ThingKey, prevState.TimeLineSettings);
                        return prevState;
                    });
            } else if (portKey == "set") {
                if (isValid)
                    this.setState((prevState) => {
                        prevState.TimeLineSettings.TimeLineItemStruct = [];
                        prevState = this.updateStateFromData(prevState, portState);
                        this.StateStorage.SetState(this.props.ThingKey, prevState.TimeLineSettings);
                        return prevState;
                    });
            }
        }
        catch (ex) {
            DebugEx.TraceError(ex, "[TimeLineWidget] Failed to parse json data.");
        }
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
interface TimeLineWidgetSettingsState {
    WidgetData: TimeLineData;
}

class TimeLineWidgetSettings extends Component<IWidgetSettingsProps<TimeLineData>, TimeLineWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<TimeLineData>) {
        super(props);

        this.state = {
            WidgetData: _.cloneDeep(this.props.WidgetData)
        };
    }
    // ------------------------------------------------------------------------------------------------
    render() {

        let settings: SettingsGroup<TimeLineData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Events",
                        tooltip: "Set name, icon and theme for an event",
                        dataPath: "Events",
                        dialogStaticSize: { w: 0.7, h: 0.8 },
                        itemsInfo: [
                            {
                                label: "Name",
                                type: SettingsType.String,
                                dataPath: "name"
                            },
                            {
                                label: "Icon",
                                type: SettingsType.DropDown,
                                dataPath: "icon",
                                items: [],
                                itemsFunc: data => _.map(data.EventIconList, ei => ei)
                            },
                            {
                                label: "Theme",
                                type: SettingsType.DropDown,
                                dataPath: "iconTheme",
                                items: [],
                                itemsFunc: data => _.map(data.EventColorList, ei => ei)
                            },
                        ],
                        createNewItem: () => {
                            return {
                                name: "New Event",
                                icon: "fa-warning",
                                iconTheme: "bg-yellow"
                            };
                        }
                    },
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Icons",
                        tooltip: 'Sets label and icon for every alert.',
                        dataPath: "EventIconList",
                        dialogStaticSize: { w: 0.7, h: 0.8 },
                        itemsInfo: [
                            {
                                label: "Label",
                                type: SettingsType.String,
                                dataPath: "label"
                            },
                            {
                                label: "Value",
                                type: SettingsType.String,
                                dataPath: "value",
                            },
                        ],
                        createNewItem: () => {
                            return {
                                label: "",
                                value: ""
                            };
                        }
                    },
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Theme Color",
                        tooltip: 'Sets label and color for every theme color.',
                        dataPath: "EventColorList",
                        dialogStaticSize: { w: 0.7, h: 0.8 },
                        itemsInfo: [
                            {
                                label: "Label",
                                type: SettingsType.String,
                                dataPath: "label"
                            },
                            {
                                label: "Value",
                                type: SettingsType.String,
                                dataPath: "value",
                            },
                        ],
                        createNewItem: () => {
                            return {
                                label: "",
                                value: ""
                            };
                        }
                    },
                    getButtonSettingsListInfo("Footer Buttons", "FooterButtons", false),
                    {
                        type: SettingsType.Boolean,
                        label: "Limited Events",
                        tooltip: 'If enabled, you can set a limit to the amount of events.',
                        dataPath: "LimitedEvents"
                    },
                    {
                        type: SettingsType.Number,
                        label: "Max Events",
                        tooltip: "The maximum number of events.",
                        dataPath: 'MaxEvents',
                        minValue: 5,
                        maxValue: 20,
                        isHidden: (data) => !data.LimitedEvents,
                        oneLine: true,
                    },
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                this.setState({ WidgetData: data },
                    () => {
                        if (isDefined(this.props.onWidgetDataUpdate))
                            this.props.onWidgetDataUpdate(this.state.WidgetData);
                    }
                )
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// TimeLine Description interface
export class TimeLineWidget extends IWidgetThingsClass<TimeLineData> {
    // ------------------------------------------------------------------------------------------------
    public Render(): ReactNode {
        return <TimeLine />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <TimeLineWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add ports
        this.getPorts(this.WidgetData).forEach((port) => {
            thing.AddPort(new WebThingPort(port, WebThingPortType.Trigger), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = this.getPorts(wData).map((port, index) => {
            return new WebThingPort(
                port,
                WebThingPortType.Trigger,
                (data) => this.ValidateValues(port.PortKey, data)
            );
        });
        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    private getPorts(savedSettings: TimeLineData): Plegma.Port[] {
        let ports: Plegma.Port[] = [];

        ports = ports.concat([{
            Name: "Set",
            PortKey: "set",
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ""
        },
        {
            Name: "Add",
            PortKey: "add",
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ""
        },
        {
            Name: "Reset",
            PortKey: "reset",
            Type: Plegma.ePortType.String,
            ioDirection: Plegma.ioPortDirection.Input,
            ConfFlags: Plegma.ePortConf.IsTrigger,
            State: ""
        }]);

        if (savedSettings != null) {
            // Add the ports
            ports = ports.concat(savedSettings.FooterButtons.map((btn, index) => {
                return {
                    Name: btn.Label,
                    PortKey: btn.Key,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: ""
                };
            }));
        }

        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TimeLineData {
        return {
            EventIconList: [],
            EventColorList: [],
            Events: [],
            FooterButtons: [],
            LimitedEvents: false,
            MaxEvents: 10
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return TimeLineWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: TimeLineData = TimeLineWidget.GetDefaultWidgetDataStatic();

        // fill sample event icon list
        data.EventIconList = [
            { label: "Warning", value: "fa fa-warning" },
            { label: "Success", value: "fa fa-check" },
            { label: "Error", value: "fa fa-close" },
            { label: "User", value: "fa fa-user" },
            { label: "Message", value: "fa fa-commenting" }
        ];

        // fill sample event color list
        data.EventColorList = [
            { label: "Blue", value: "bg-blue" },
            { label: "Green", value: "bg-green" },
            { label: "Red", value: "bg-red" },
            { label: "Yellow", value: "bg-yellow" },
            { label: "Black", value: "bg-black" }
        ];

        // fill sample events
        data.Events = [
            { name: "Error", icon: "fa fa-close", iconTheme: "bg-red" },
            { name: "Warning", icon: "fa fa-warning", iconTheme: "bg-yellow" },
            { name: "Message", icon: "fa fa-commenting", iconTheme: "bg-green" },
            { name: "User", icon: "fa fa-user", iconTheme: "bg-blue" }
        ];

        // fill sample footer buttons
        data.FooterButtons = [
            { Key: "ReadMore", Flat: false, Label: "Read More", Type: ButtonThemeType.Primary, Color: 'rgba(0,0,0,1)', FontColor: "white", Uppercase: false },
            { Key: "Delete", Flat: false, Label: "Delete", Type: ButtonThemeType.Secondary, Color: 'rgba(0,0,0,1)', FontColor: "white", Uppercase: false }
        ];

        return {
            Title: "TimeLine",
            WidgetTitle: "",
            Key: "TimeLineWidget",
            Type: "TimeLineWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/timeline_widget.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    GetDefaultValues(): { [key: string]: any } {
        return {
            "set": [],
            "add": [],
            "reset": false
        };
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == "add" || key == "set")
            return TimeLineItemJsonSchemeValidator().DataValidateString(value);
        else if (key == "reset")
            if (typeof (value) === "boolean")
                return { valid: true, errors: "" };
            else
                return { valid: false, errors: "Reset value must be boolean." }
        return { valid: true, errors: "" };
    };
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: TimeLineData): TimeLineData {
        // assign `Color` field at each button (if not defined)
        if (data.FooterButtons.length > 0 &&
            IsNullOrEmpty(data.FooterButtons[0].Color)) {
            data.FooterButtons.forEach((btn) => {
                btn.Color = 'rgb(0, 188, 212)';
            })
        }
        if (data.FooterButtons.length > 0 &&
            IsNullOrEmpty(data.FooterButtons[0].FontColor)) {
            data.FooterButtons.forEach((btn) => {
                if (btn.Flat)
                    btn.FontColor = btn.Color;
                else
                    btn.FontColor = 'white';
            })
        }
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    GetLocalizationDictionary(): LocalizationMap {
        const res = {};

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.FooterButtons, (button) => {
            if (!IsNullOrEmpty(button.Label)) {
                res['button_' + button.Key] = button.Label;
            }
        });

        return res;
    };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================